import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PageWithPermissions, PageAction, PageId, ActionCode } from 'types/index';
import { settingsService } from 'services/settings.service';
import { tokenStorage } from 'utils/storage';

interface PermissionsContextType {
  pages: PageWithPermissions[];
  isLoading: boolean;
  error: string | null;
  hasPermission: (pageId: PageId, actionCode: ActionCode) => boolean;
  getPagePermissions: (pageId: PageId) => PageAction[];
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const [pages, setPages] = useState<PageWithPermissions[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPermissions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const pagesWithPermissions = await settingsService.getPagesWithPermissions();
      setPages(pagesWithPermissions);
    } catch (err: any) {
      setError(err?.message || 'Failed to load permissions');
      console.error('Error loading permissions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { 
    const token = tokenStorage.getAccessToken(); 
    if (token) {
      loadPermissions();
    } else {
      setPages([]);
    }

    const handler = () => {
      const t = tokenStorage.getAccessToken();
      if (t) {
        loadPermissions();
      } else {
        setPages([]);
      }
    };

    window.addEventListener('auth:token-changed', handler as EventListener);
    return () => {
      window.removeEventListener('auth:token-changed', handler as EventListener);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (!tokenStorage.hasValidToken()) return;
      if (document.visibilityState === 'visible') {
        loadPermissions();
      }
    };

    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, []);

  const hasPermission = (pageId: PageId, actionCode: ActionCode): boolean => {
    const page = pages.find(p => p.id === pageId);
    if (!page) return false;
    
    const action = page.actions.find(a => a.actionCode === actionCode);
    return action?.hasPermission || false;
  };

  const getPagePermissions = (pageId: PageId): PageAction[] => {
    const page = pages.find(p => p.id === pageId);
    return page?.actions || [];
  };

  const refreshPermissions = async () => {
    await loadPermissions();
  };

  const value: PermissionsContextType = {
    pages,
    isLoading,
    error,
    hasPermission,
    getPagePermissions,
    refreshPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const usePermission = (pageId: PageId, actionCode: ActionCode): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(pageId, actionCode);
};
